// A comment thread as people read and answer it. The side panel and a pin's
// popover show the same thing, so it is one component.

import { useState } from "react";
import { Avatar } from "../ui/Avatar.jsx";
import { Check } from "../ui/icons.jsx";
import { exactTime, relativeTime } from "../ui/format.js";

/**
 * A text box that sends on Enter and breaks the line on Shift+Enter. It keeps
 * what was typed when the send fails, and says why.
 */
export const ReplyBox = ({ placeholder, submitLabel, onSubmit, onCancel, hint, children, autoFocus = false }) => {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const send = async () => {
    const body = text.trim();
    if (!body || busy) return;
    setBusy(true);
    setError(null);
    try {
      await onSubmit(body);
      setText("");
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="reply-box">
      <textarea
        className="textarea reply-input"
        rows={1}
        value={text}
        placeholder={placeholder}
        maxLength={4000}
        autoFocus={autoFocus}
        disabled={busy}
        aria-label={placeholder}
        onChange={(event) => setText(event.target.value)}
        onKeyDown={(event) => {
          // Enter while an input method is choosing a character must not send.
          if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
            event.preventDefault();
            void send();
          }
        }}
      />
      {error ? <p className="reply-error">{error}</p> : null}
      <div className="reply-actions">
        {hint ? <span className="reply-hint">{hint}</span> : null}
        {children}
        <span className="spacer" />
        {onCancel ? (
          <button type="button" className="btn ghost small" onClick={onCancel}>
            Cancel
          </button>
        ) : null}
        <button
          type="button"
          className="btn primary small"
          disabled={busy || text.trim().length === 0}
          onClick={() => void send()}
        >
          {submitLabel}
        </button>
      </div>
    </div>
  );
};

const Message = ({ comment }) => (
  <div className="comment">
    <Avatar user={{ name: comment.author.name }} size={24} />
    <div className="comment-main">
      <div className="comment-head">
        <span className="comment-author">{comment.author.name}</span>
        {comment.author.kind === "agent" ? <span className="comment-kind">agent</span> : null}
        <span className="comment-time" title={exactTime(comment.createdAt)}>
          {relativeTime(comment.createdAt)}
        </span>
      </div>
      <p className="comment-body">{comment.body}</p>
    </div>
  </div>
);

/**
 * @param {{thread: {root: object, replies: object[]}, where: string, gone: boolean,
 *   onReply: (rootId: string, body: string) => Promise, onResolve: (rootId: string, resolved: boolean) => Promise}} props
 */
export const Thread = ({ thread, where, gone, onReply, onResolve }) => {
  const { root, replies } = thread;
  const [error, setError] = useState(null);

  const toggle = async () => {
    setError(null);
    try {
      await onResolve(root.id, !root.resolved);
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="thread">
      <div className={`thread-where${gone ? " gone" : ""}`} title={where}>
        {where}
      </div>
      <Message comment={root} />
      {replies.map((reply) => (
        <Message key={reply.id} comment={reply} />
      ))}
      <ReplyBox placeholder="Reply" submitLabel="Reply" onSubmit={(body) => onReply(root.id, body)}>
        <button type="button" className="btn ghost small" onClick={() => void toggle()}>
          {root.resolved ? (
            "Reopen"
          ) : (
            <>
              <Check />
              Resolve
            </>
          )}
        </button>
      </ReplyBox>
      {error ? <p className="reply-error">{error}</p> : null}
    </div>
  );
};
